import type { AgenticExportDeepLinkConfig, AgenticExportDeepLinkTarget } from '../AskAsperAgentic.types';
import { scrollElementToStartWithinContainer } from './scrollWithinContainer';

/**
 * Fixed key used to lightly obfuscate the user id embedded in export deep links.
 * This is NOT cryptographic security — it only keeps the email out of plain sight
 * in the URL while remaining reversible client-side for an ownership check.
 */
const DEEP_LINK_USER_KEY = 'AskAsperDeepLinkUserKey';

/** URL-safe Base64 helpers (browser `btoa`/`atob` produce `+`, `/`, `=`). */
function toUrlSafeBase64(value: string): string {
    return value.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromUrlSafeBase64(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return padded;
}

function xorWithKey(value: string): string {
    let out = '';
    for (let i = 0; i < value.length; i++) {
        const charCode = value.charCodeAt(i) ^ DEEP_LINK_USER_KEY.charCodeAt(i % DEEP_LINK_USER_KEY.length);
        out += String.fromCharCode(charCode);
    }
    return out;
}

/**
 * Obfuscate a user id (e.g. email) for embedding in an export deep link.
 * Reversible via {@link decodeAgenticDeepLinkUser}. Returns `undefined` for empty input.
 */
export function encodeAgenticDeepLinkUser(userId: string | undefined | null): string | undefined {
    const trimmed = userId?.trim();
    if (!trimmed) return undefined;
    try {
        // encodeURIComponent first so non-ASCII emails survive the btoa byte range.
        const xored = xorWithKey(unescape(encodeURIComponent(trimmed)));
        return toUrlSafeBase64(btoa(xored));
    } catch {
        return undefined;
    }
}

/** Reverse {@link encodeAgenticDeepLinkUser}. Returns `undefined` when the token can't be decoded. */
export function decodeAgenticDeepLinkUser(token: string | undefined | null): string | undefined {
    const trimmed = token?.trim();
    if (!trimmed) return undefined;
    try {
        const decoded = atob(fromUrlSafeBase64(trimmed));
        const restored = decodeURIComponent(escape(xorWithKey(decoded)));
        return restored || undefined;
    } catch {
        return undefined;
    }
}

/** Target artefact for an export deep link: exactly one of `taskId` / `runId`, plus optional encoded user. */
export interface AgenticExportDeepLinkUrlTarget {
    taskId?: string;
    runId?: string;
    /** Already-encoded user id (see {@link encodeAgenticDeepLinkUser}). */
    encodedUserId?: string;
}

/**
 * Builds a deep link URL for exported artefacts.
 * Format: `{hostLink}{endpoint}?threadId=…&(taskId|runId)=…[&userId=…]`
 */
export function buildAgenticExportDeepLinkUrl(
    config: AgenticExportDeepLinkConfig,
    threadId: string,
    target: AgenticExportDeepLinkUrlTarget,
): string {
    const host = config.hostLink.replace(/\/+$/, '');
    const path = config.endpoint.startsWith('/') ? config.endpoint : `/${config.endpoint}`;
    const params = new URLSearchParams({ threadId });
    if (target.runId) {
        params.set('runId', target.runId);
    } else if (target.taskId) {
        params.set('taskId', target.taskId);
    }
    const encodedUserId = target.encodedUserId ?? config.encodedUserId;
    if (encodedUserId) {
        params.set('userId', encodedUserId);
    }
    return `${host}${path}?${params.toString()}`;
}

function readSearchParam(
    params: URLSearchParams | Record<string, string | undefined | null>,
    key: string,
): string | undefined {
    const raw =
        params instanceof URLSearchParams
            ? params.get(key)
            : params[key];
    const trimmed = raw?.trim();
    return trimmed || undefined;
}

/**
 * Parse `threadId`, `taskId` / `runId` and `userId` from URL search params
 * (host reads route, passes result here). A valid target needs `threadId`
 * plus at least one of `taskId` / `runId`.
 */
export function parseAgenticExportDeepLinkTarget(
    params: URLSearchParams | Record<string, string | undefined | null>,
): AgenticExportDeepLinkTarget | null {
    const threadId = readSearchParam(params, 'threadId');
    const taskId = readSearchParam(params, 'taskId');
    const runId = readSearchParam(params, 'runId');
    const encodedUserId = readSearchParam(params, 'userId');
    if (!threadId || (!taskId && !runId)) return null;
    return { threadId, taskId, runId, encodedUserId };
}

/** True when an accordion (or its children) contains the deep-link task id. */
export function accordionShouldForceExpandForDeepLink(
    item: { id: string; children?: { id: string }[] },
    deepLinkTaskId?: string,
): boolean {
    if (!deepLinkTaskId) return false;
    if (item.id === deepLinkTaskId) return true;
    return item.children?.some((child) => child.id === deepLinkTaskId) ?? false;
}

const DEEP_LINK_SCROLL_MAX_ATTEMPTS = 30;
const DEEP_LINK_SCROLL_RETRY_MS = 200;

/**
 * Scroll the right-panel scroll container to `[data-task-id="{taskId}"]`, retrying while
 * messages/accordions mount after a thread switch.
 */
export function scrollAgenticRightPanelToTask(
    scrollContainer: HTMLElement | null,
    taskId: string,
    onComplete: (found: boolean) => void,
): () => void {
    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
        if (cancelled) return;

        const el = scrollContainer?.querySelector<HTMLElement>(`[data-task-id="${taskId}"]`);
        if (el && scrollContainer) {
            scrollElementToStartWithinContainer(el, scrollContainer, 'smooth');
            onComplete(true);
            return;
        }

        attempts += 1;
        if (attempts >= DEEP_LINK_SCROLL_MAX_ATTEMPTS) {
            onComplete(false);
            return;
        }

        window.setTimeout(tryScroll, DEEP_LINK_SCROLL_RETRY_MS);
    };

    window.requestAnimationFrame(tryScroll);

    return () => {
        cancelled = true;
    };
}

/**
 * Scroll the right-panel scroll container to `[data-run-id="{runId}"]` (the start of a
 * question's response block), retrying while messages/accordions mount after a thread switch.
 */
export function scrollAgenticRightPanelToRun(
    scrollContainer: HTMLElement | null,
    runId: string,
    onComplete: (found: boolean) => void,
): () => void {
    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
        if (cancelled) return;

        const el = scrollContainer?.querySelector<HTMLElement>(`[data-run-id="${runId}"]`);
        if (el && scrollContainer) {
            scrollElementToStartWithinContainer(el, scrollContainer, 'smooth');
            onComplete(true);
            return;
        }

        attempts += 1;
        if (attempts >= DEEP_LINK_SCROLL_MAX_ATTEMPTS) {
            onComplete(false);
            return;
        }

        window.setTimeout(tryScroll, DEEP_LINK_SCROLL_RETRY_MS);
    };

    window.requestAnimationFrame(tryScroll);

    return () => {
        cancelled = true;
    };
}
